import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { MAX_BROADCAST_NAME_LENGTH, validateBroadcast } from "@velizhanin-dev/bot-scenario"
import { AnimatePresence, motion } from "framer-motion"
import { CalendarClock, CircleCheck, Send, Smartphone, Trash2 } from "lucide-react"
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import type { AudienceFilter } from "@/entities/audience"
import { memberName, plural } from "@/entities/audience"
import { getNiches } from "@/entities/category"
import {
	type Broadcast,
	broadcastKeys,
	broadcastMediaSource,
	formatWhen,
	sendBroadcast,
	testBroadcast,
	totalOf,
	type UpdateBroadcastBody,
	updateBroadcast,
	watchBroadcast,
} from "@/entities/broadcast"
import { describeFilter, SegmentFilters } from "@/features/filter-audience"
import { SavedFilterChips, SaveFilterButton } from "@/features/save-audience-filter"
import { BroadcastForm, BroadcastPreview, TIME_ZONE } from "@/features/send-broadcast"
import { UnsavedChangesGuard } from "@/shared/hooks/use-unsaved-changes"
import { listItem } from "@/shared/lib/motion"
import { createSaveQueue, type SaveState } from "@/shared/lib/save-queue"
import { cn } from "@/shared/lib/utils"
import { AnimatedNumber } from "@/shared/ui/animated-number"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { LoadSwap } from "@/shared/ui/load-swap"
import { Reveal } from "@/shared/ui/reveal"
import { RowActionsMenu } from "@/shared/ui/row-actions-menu"
import { useRecipientCount, useRecipientSample } from "../lib/use-recipient-count"
import { useDeleteBroadcast } from "./delete-button"

const USERS: [string, string, string] = ["пользователю", "пользователям", "пользователям"]
const AUTOSAVE_DELAY_MS = 700

const SAVE_TEXT: Record<SaveState, string> = {
	saved: "сохранено",
	dirty: "есть изменения",
	saving: "сохраняем",
	error: "не удалось сохранить",
}

/** Подпись, которая сменяется перекрёстно, а не подменяется: текст кнопки, строка статуса. */
function Swap({ id, children, className }: { id: string; children: ReactNode; className?: string }) {
	return (
		<LoadSwap loading={false} id={id} autoHeight={false} className={className}>
			{children}
		</LoadSwap>
	)
}

/**
 * Черновик рассылки. Шапка как у материала в «Контенте»: надзаголовок с состоянием
 * сохранения, название правится прямо в ней. Форма сохраняется сама через паузу в наборе,
 * справа превью и сводка с отправкой, она едет вместе с прокруткой.
 */
export interface DraftShellParts {
	eyebrow: string
	title: ReactNode
	actions: ReactNode
	children: ReactNode
}

export function DraftEditor({
	botId,
	broadcast,
	shell,
}: {
	botId: string
	broadcast: Broadcast
	/** Шапка страницы вокруг редактора: её рисует страница, у неё свой каркас раздела. */
	shell: (parts: DraftShellParts) => ReactNode
}) {
	const queryClient = useQueryClient()
	const niches = useQuery({ queryKey: ["niches"], queryFn: getNiches, staleTime: 5 * 60_000 })
	const { confirm } = useConfirm()
	const remove = useDeleteBroadcast(botId, broadcast)
	const key = broadcastKeys.detail(botId, broadcast.id)
	const [name, setName] = useState(broadcast.name)
	const [content, setContent] = useState(broadcast.content)
	const [filter, setFilter] = useState<AudienceFilter>(broadcast.filter)
	/** datetime-local во времени устройства; пусто: отправить сразу. */
	const [sendAt, setSendAt] = useState("")
	const [saveState, setSaveState] = useState<SaveState>("saved")
	const timer = useRef<number | undefined>(undefined)
	const mediaSource = useMemo(() => broadcastMediaSource(botId), [botId])
	const [queue] = useState(() =>
		createSaveQueue<UpdateBroadcastBody>(async (patch) => {
			queryClient.setQueryData(
				broadcastKeys.detail(botId, broadcast.id),
				await updateBroadcast(botId, broadcast.id, patch),
			)
		}, setSaveState),
	)
	const flush = () => {
		window.clearTimeout(timer.current)
		return queue.flush()
	}
	useEffect(
		() => () => {
			window.clearTimeout(timer.current)
			void queue.flush().catch(() => undefined)
		},
		[queue],
	)
	useEffect(() => {
		const guard = (event: BeforeUnloadEvent) => {
			if (queue.hasPending) event.preventDefault()
		}
		window.addEventListener("beforeunload", guard)
		return () => window.removeEventListener("beforeunload", guard)
	}, [queue])
	const schedule = (patch: UpdateBroadcastBody) => {
		queue.schedule(patch)
		window.clearTimeout(timer.current)
		timer.current = window.setTimeout(
			() => void queue.flush().catch(() => undefined),
			AUTOSAVE_DELAY_MS,
		)
	}

	const count = useRecipientCount(botId, filter, true)
	const sample = useRecipientSample(botId, filter, true)
	// Пока фильтр пересчитывается, показываем прошлое число, а отправку держим до свежего.
	const countReady = !count.isFetching && !count.isPlaceholderData
	const recipients = totalOf(count.data)
	const total = recipients ?? 0
	const flowIds = useMemo(
		() => new Set(broadcast.context.flows.map((flow) => flow.id)),
		[broadcast.context.flows],
	)
	const scheduledAt = sendAt ? new Date(sendAt) : null
	const scheduleInvalid =
		!!scheduledAt &&
		(Number.isNaN(scheduledAt.getTime()) || scheduledAt.getTime() <= Date.now() + 60_000)
	const issues = useMemo(() => {
		const found = validateBroadcast(content, flowIds).issues.map((issue) => issue.message)
		if (!name.trim()) found.unshift("Назовите рассылку")
		if (scheduleInvalid) found.push("Выберите время отправки в будущем")
		// Пустой список api отбрасывает: без этой проверки рассылка ушла бы всем.
		if (filter.sessionIds && filter.sessionIds.length === 0)
			found.push("Отметьте пользователей, которым отправить")
		return found
	}, [content, flowIds, name, scheduleInvalid, filter.sessionIds])

	const send = useMutation({
		mutationFn: async (scheduledAt?: string) => {
			await flush()
			return sendBroadcast(botId, broadcast.id, scheduledAt ? { scheduledAt } : {})
		},
		onSuccess: async (next) => {
			queryClient.setQueryData(key, next)
			watchBroadcast({ botId, id: next.id, name: next.name })
			await queryClient.invalidateQueries({ queryKey: broadcastKeys.list(botId) })
			toast.success(
				next.status === "SCHEDULED"
					? `Рассылка запланирована на ${next.scheduledAt ? formatWhen(next.scheduledAt) : "выбранное время"}`
					: "Рассылка отправляется",
			)
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось отправить рассылку")),
	})
	const test = useMutation({
		mutationFn: async () => {
			await flush()
			return testBroadcast(botId, broadcast.id)
		},
		onSuccess: ({ botUsername }) =>
			toast.success(`Проверка придёт вам в Telegram от @${botUsername}`),
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось отправить проверку")),
	})
	const blocked = issues.length > 0 || send.isPending || saveState === "error"
	const description = describeFilter(filter, broadcast.context.tags, niches.data)
	const sendLabel =
		scheduledAt && !scheduleInvalid
			? `Запланировать на ${formatWhen(scheduledAt.toISOString())}`
			: scheduledAt
				? "Запланировать"
				: "Отправить сейчас"
	const sampleText =
		recipients && sample.data?.length
			? `Например: ${sample.data.map((member) => memberName(member)).join(", ")}${recipients > sample.data.length ? " и другие" : ""}`
			: ""

	const onSend = () => {
		const audience = `${description}, сейчас это ${total} ${plural(total, ["человек", "человека", "человек"])}.`
		const iso = scheduledAt?.toISOString()
		confirm({
			title: iso ? `Запланировать на ${formatWhen(iso)}?` : `Отправить ${total} ${plural(total, USERS)}?`,
			message: iso
				? `${audience} Точный список соберётся ${formatWhen(iso)} по времени ${TIME_ZONE}. До этого рассылку можно снять с расписания.`
				: `${audience} Отправленное отозвать нельзя, можно только остановить остаток.`,
			confirmText: iso ? "Запланировать" : "Отправить",
			onConfirm: async () => {
				await send.mutateAsync(iso).catch(() => undefined)
			},
		})
	}

	return shell({
		eyebrow: `Черновик · ${SAVE_TEXT[saveState]}`,
		title: (
				<input
					aria-label="Название рассылки"
					title="Название видно только в админке"
					placeholder="Назовите рассылку"
					maxLength={MAX_BROADCAST_NAME_LENGTH}
					value={name}
					onChange={(event) => {
						setName(event.target.value)
						if (event.target.value.trim()) schedule({ name: event.target.value.trim() })
					}}
					className="-mx-2 block h-9 w-[calc(100%+1rem)] min-w-0 rounded-lg bg-transparent px-2 font-bold tracking-tight transition-colors outline-none placeholder:text-muted-foreground/50 hover:bg-secondary/40 focus:bg-secondary/60 sm:h-10"
				/>
		),
		actions: (
				<>
					<Button
						variant="outline"
						className="rounded-xl"
						disabled={issues.length > 0 || test.isPending || saveState === "error"}
						isLoading={test.isPending}
						title="Сообщение придёт только вам, получатели его не увидят"
						onClick={() => test.mutate()}
					>
						{test.isPending ? null : <Smartphone />}
						Отправить себе
					</Button>
					<RowActionsMenu
						label="Ещё действия с рассылкой"
						actions={[{ label: "Удалить черновик", icon: Trash2, destructive: true, onSelect: remove }]}
					/>
				</>
		),
		children: (
			<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
				<UnsavedChangesGuard dirty={saveState !== "saved"} />
				<fieldset disabled={send.isPending} className="min-w-0">
					<BroadcastForm
						botId={botId}
						content={content}
						filter={filter}
						context={broadcast.context}
						recipients={recipients}
						counting={!countReady}
						onContent={(next) => {
							setContent(next)
							schedule({ content: next })
						}}
						onFilter={(next) => {
							setFilter(next)
							schedule({ filter: next })
						}}
						sendAt={sendAt}
						onSendAt={setSendAt}
						filterDescription={description}
						renderSegmentFilters={(value, onChange, hasContext) => (
							<SegmentFilters
								value={value}
								onChange={onChange}
								hideEmptyHint={hasContext}
								variables={broadcast.context.variables}
							/>
						)}
						savedFilters={
							<div className="flex flex-wrap items-center gap-2">
								<SavedFilterChips
									botId={botId}
									onApply={(next) => {
										setFilter(next)
										schedule({ filter: next })
									}}
								/>
								<SaveFilterButton botId={botId} filter={filter} suggestedName={description} />
							</div>
						}
					/>
				</fieldset>
				<aside className="space-y-3 lg:sticky lg:top-4">
					<BroadcastPreview
						botUsername={broadcast.context.botUsername}
						content={content}
						mediaSource={mediaSource}
					/>
					<section className="rounded-2xl border border-border bg-card p-5">
						<div className="flex items-baseline justify-between gap-2">
							<span className="text-sm text-muted-foreground">Получатели</span>
							<span
								className={cn(
									"text-2xl font-semibold tabular-nums transition-opacity duration-200",
									!countReady && "opacity-50",
								)}
							>
								{recipients === undefined ? "–" : <AnimatedNumber value={recipients} />}
							</span>
						</div>
						<Reveal show={!!sampleText} className="pt-2">
							<Swap id={sampleText || "none"}>
								<p className="text-xs text-muted-foreground">{sampleText}</p>
							</Swap>
						</Reveal>
						<ul className="pt-1">
							<AnimatePresence initial={false}>
								{issues.length === 0 ? (
									<motion.li key="ready" {...listItem}>
										<p className="flex items-center gap-1.5 pt-2 text-xs text-emerald-400">
											<CircleCheck className="size-3.5" />
											Готово к отправке
										</p>
									</motion.li>
								) : (
									issues.map((issue) => (
										<motion.li key={issue} {...listItem}>
											<p className="pt-2 text-xs text-amber-300">{issue}</p>
										</motion.li>
									))
								)}
							</AnimatePresence>
						</ul>
						<Button
							className="mt-4 w-full rounded-xl"
							disabled={blocked || !countReady || recipients === undefined || total === 0}
							isLoading={send.isPending}
							onClick={onSend}
						>
							{send.isPending ? null : scheduledAt ? <CalendarClock /> : <Send />}
							<Swap id={sendLabel} className="inline-block">
								<span>{sendLabel}</span>
							</Swap>
						</Button>
						<Reveal show={saveState === "error"} className="pt-2">
							<Button
								variant="outline"
								className="w-full rounded-xl"
								onClick={() => void flush().catch(() => undefined)}
							>
								Повторить сохранение
							</Button>
						</Reveal>
					</section>
				</aside>
			</div>
		),
	})
}
